/**
 * Webhook-inbox widgets (#1805 slice A2).
 *
 * Exactly one widget, and it exists to re-express `fields[].copyable` — the
 * legacy carrier on `provider_event_id` and `payload_hash`, which has no v2
 * equivalent. It is the same trap `features/audit/widgets.tsx` documents for
 * `auditTraceId`, so the recipe is copied rather than re-derived:
 *
 *   `copyable` renders through the id widget whatever the field's declared type
 *   (the retired `schema-utils` short-circuited to `IdWidgetCell` ahead of the
 *   resolved widget; `copyable` is still declared on the legacy field at
 *   `packages/resource-kit-controllers/react/contracts.ts::EntityFieldBase.copyable`), but it EXPORTS
 *   through the field's own type. So the obvious
 *   translation — re-type both columns `f.id()` — produces byte-identical
 *   markup while silently moving the CSV projection from the text type's
 *   `'' → -` to the id type's `'' → ''`.
 *
 * The widget therefore borrows the id type's DISPLAY and declares NO `csvText`,
 * which leaves `formatFieldTextForExport` falling through to the field's `text`
 * type exactly as before. Both columns keep `type: 'text'`; that type IS the
 * export contract here, not an implementation detail.
 *
 * No `@services/*` import, deliberately: `src/features/widgets.ts` is evaluated
 * by `src/test-setup.ts` for EVERY suite, ahead of any `vi.mock`, so a service
 * reached from a widget module is instantiated against the real `@lib/api`
 * before a test can replace it.
 */
import { failHard } from '@resource-kit/core';
import { FIELD_TYPE_WIDGETS } from '@resource-kit/ui/react';
import { registerWidget } from '@resource-kit/controllers';
import type { FieldType, Widget } from '@resource-kit/model';

declare module '@resource-kit/core' {
  interface WidgetRegistry {
    webhookCopyableText: { types: 'text'; modes: 'list' };
  }
}

/** The field type's own widget — level 3 of the resolution order. */
function typeWidget(type: FieldType): Widget {
  return (
    FIELD_TYPE_WIDGETS.get(type) ??
    failHard(`no default widget is registered for field type "${type}"`)
  );
}

/** Called by the feature barrel, which the composition root calls once. */
export function registerWebhookWidgets(): void {
  registerWidget('webhookCopyableText', {
    // Display only — see the file header for why `csvText` is absent.
    display: typeWidget('id').display,
    // No `input`: resolution refills it from the field type default
    // (ADR-0030).
    supportedTypes: ['text'],
    modes: ['list'],
  });
}
